import { Component, Input, Output, EventEmitter, ViewChildren, QueryList, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import {
	ActivityCategory,
	ActivityTypeId,
	ActivityType,
	ActivityTypeCollection,
	ActivitySubType,
	ActivitySubTypeId,
	CircularArray
} from '../../domain';
import { ConsoleService } from '../console.service';
import { TooltipService } from '../../widgets/tooltip.service';

/**
 * Menu for picking the sub type of a new activity.
 *
 * Accessibility notes:
 *  - Categories follow the WAI-ARIA "tabs" pattern (roving tabindex, arrow keys move between tabs).
 *  - Sub type descriptions are expanded inline with a real button (aria-expanded / aria-controls),
 *    so they work with touch, keyboard and screen readers instead of relying on mouse hover.
 *  - Layout is a single-column Bootstrap list on small screens and two columns on large screens.
 */
@Component({
	selector: 'activity-type-menu',
	templateUrl: './activity-type-menu.component.html',
	styleUrls: ['./activity-type-menu.component.scss']
})
export class ActivityTypeMenuComponent implements OnInit, OnDestroy {
	private static _nextInstanceId = 0;

	@Output() subTypeClick = new EventEmitter<ActivitySubType>();
	@Input() activityTypes: ActivityTypeCollection;
	@Input("activityCategories") categories: CircularArray<ActivityCategory>;
	@Input() startingActivityCategoryId: number = 0;
	@ViewChildren('categoryTab') categoryTabs: QueryList<ElementRef<HTMLButtonElement>>;
	@ViewChildren('subTypeSelect') subTypeButtons: QueryList<ElementRef<HTMLButtonElement>>;

	ActivitySubTypeId = ActivitySubTypeId;

	// Unique prefix so ids stay unique if more than one menu is ever on the page.
	private readonly _idPrefix = `activity-type-menu-${ActivityTypeMenuComponent._nextInstanceId++}`;
	private readonly _expandedSubTypes = new Set<ActivitySubTypeId>();
	private _categoryChanges: Subscription;

	/** Activity types of the current category, split into columns for the large-screen layout. */
	typeColumns: ActivityType[][] = [];

	constructor(
		private console: ConsoleService,
		public tooltipSvc: TooltipService
	) { }

	ngOnInit() {
		this.console.log('menu component:', this);
		this.categories.tryMoveToIndex(this.startingActivityCategoryId);
		// On category change: collapse everything so the new panel starts compact, and lay out its cards.
		this._categoryChanges = this.categories.changes.subscribe(category => {
			this._expandedSubTypes.clear();
			this.typeColumns = ActivityTypeMenuComponent._splitIntoColumns(category.activityTypes.all, 2);
		});
	}

	ngOnDestroy() {
		if (this._categoryChanges) {
			this._categoryChanges.unsubscribe();
		}
	}

	//TODO: Add to Airtable
	definitions = new Map<ActivityTypeId, string>([
		[ActivityTypeId.Extension, 'An extension activity is directly related to your own program clientele'],
		[ActivityTypeId.UniversityAndPublicService, 'Per the E-Book, Services to the University, ANR, and CE are a critical part of an Academic appointee\'s responsibilities. Contributions to community and beyond are also expectations of the academic appointment, representing UC and the academic discipline.'],
		[ActivityTypeId.ProfessionalCompetence, "Per the E-Book, Competence in the subject matter appropriate to the discipline is fundamental to individual success, and to the success and progress of UC ANR."],
		[ActivityTypeId.AppliedResearchAndCreative, 'Useful for Federal and organizational reporting.'],
		[ActivityTypeId.Outreach, 'All Reasonable Effort is defined as the utilization of at least three types of invitation methods to increase equitable opportunities for participation in your program and foster UC ANR’s Principles of Community.'],
		[ActivityTypeId.ExtensionAndOrOutreach, 'These activity types will be included in both a) Civil Rights Compliance analysis and b) Extension Activities table section of the dossier export.'],
		[ActivityTypeId.AcademicCoordination, 'Per the E-Book, review criteria for Coordination of Academic Programs include these activity types.'],
		[ActivityTypeId.Other, 'Per the E-Book, If you are including educational presentations related to your academic program that you did not directly present yourself but that were delivered by your staff or others, please include these as a separate table from those you delivered personally. Also include your role in these presentations that your staff or others delivered.']
	]);

	/**
	 * Distribute types across columns, keeping their order and balancing by size so the columns
	 * come out roughly equal. Each card's weight is its sub type count plus a couple of rows for
	 * the header. Empty columns are dropped (categories with a single type get one column).
	 */
	private static _splitIntoColumns(types: ActivityType[], columnCount: number): ActivityType[][] {
		const columns: ActivityType[][] = Array.from({ length: columnCount }, () => []);
		const weights: number[] = columns.map(() => 0);
		for (const type of types) {
			let target = 0;
			for (let i = 1; i < weights.length; i++) {
				if (weights[i] < weights[target]) {
					target = i;
				}
			}
			columns[target].push(type);
			weights[target] += type.subTypes.length + 2;
		}
		return columns.filter(column => column.length > 0);
	}

	// ---- ids used to wire up aria-controls / aria-labelledby ----

	tabId(category: ActivityCategory): string {
		return `${this._idPrefix}-tab-${category.id}`;
	}

	panelId(category: ActivityCategory): string {
		return `${this._idPrefix}-panel-${category.id}`;
	}

	typeHeadingId(type: ActivityType): string {
		return `${this._idPrefix}-type-${type.id}`;
	}

	descriptionId(subType: ActivitySubType): string {
		return `${this._idPrefix}-description-${subType.id}`;
	}

	// ---- category tabs ----

	isCurrentCategory(category: ActivityCategory): boolean {
		return category == this.categories.currentItem;
	}

	selectCategory(category: ActivityCategory) {
		this.categories.moveTo(category);
	}

	/** Used by the small-screen <select>; the value arrives as a string. */
	selectCategoryById(id: string | number) {
		const numericId = Number(id);
		for (const category of this.categories) {
			if (category.id === numericId) {
				this.categories.moveTo(category);
				return;
			}
		}
	}

	get categorySelectId(): string {
		return `${this._idPrefix}-category-select`;
	}

	onTabKeydown(event: KeyboardEvent) {
		switch (event.key) {
			case 'ArrowLeft':
				this.categories.movePrev();
				this._focusCurrentTab();
				break;
			case 'ArrowRight':
				this.categories.moveNext();
				this._focusCurrentTab();
				break;
			case 'Home':
				this.categories.tryMoveToIndex(0);
				this._focusCurrentTab();
				break;
			case 'End':
				this.categories.tryMoveToIndex(this._categoryCount() - 1);
				this._focusCurrentTab();
				break;
			case 'ArrowDown':
				// Jump from the tab into the first selectable sub type of the panel.
				this._focusSubType(0);
				break;
			default:
				return;
		}
		event.preventDefault();
		event.stopPropagation();
	}

	private _categoryCount(): number {
		let count = 0;
		for (const _ of this.categories) { count++; }
		return count;
	}

	private _focusCurrentTab() {
		// Wait a tick so the roving tabindex has been re-rendered for the new current tab.
		setTimeout(() => {
			const current = this.categoryTabs
				.map(ref => ref.nativeElement)
				.find(el => el.getAttribute('aria-selected') === 'true');
			if (current) {
				current.focus();
			}
		}, 0);
	}

	// ---- sub types ----

	onSubTypeKeydown(event: KeyboardEvent) {
		const buttons = this.subTypeButtons.map(ref => ref.nativeElement);
		const index = buttons.indexOf(event.target as HTMLButtonElement);
		if (index === -1) {
			return;
		}
		switch (event.key) {
			case 'ArrowDown':
				this._focusSubType(index + 1);
				break;
			case 'ArrowUp':
				this._focusSubType(index - 1);
				break;
			case 'Home':
				this._focusSubType(0);
				break;
			case 'End':
				this._focusSubType(buttons.length - 1);
				break;
			default:
				return;
		}
		event.preventDefault();
		event.stopPropagation();
	}

	private _focusSubType(index: number) {
		const buttons = this.subTypeButtons.map(ref => ref.nativeElement);
		if (buttons.length === 0) {
			return;
		}
		// Wrap around at both ends.
		const wrapped = ((index % buttons.length) + buttons.length) % buttons.length;
		buttons[wrapped].focus();
	}

	// ---- inline descriptions ----

	isExpanded(subType: ActivitySubType): boolean {
		return this._expandedSubTypes.has(subType.id);
	}

	toggleDescription(subType: ActivitySubType) {
		if (this._expandedSubTypes.has(subType.id)) {
			this._expandedSubTypes.delete(subType.id);
		} else {
			this._expandedSubTypes.add(subType.id);
		}
	}

	get allExpanded(): boolean {
		const subTypes = this._currentSubTypes();
		return subTypes.length > 0 && subTypes.every(st => this._expandedSubTypes.has(st.id));
	}

	toggleAllDescriptions() {
		const subTypes = this._currentSubTypes();
		if (this.allExpanded) {
			subTypes.forEach(st => this._expandedSubTypes.delete(st.id));
		} else {
			subTypes.forEach(st => this._expandedSubTypes.add(st.id));
		}
	}

	private _currentSubTypes(): ActivitySubType[] {
		return this.categories.currentItem.activityTypes.all
			.reduce((acc, type) => acc.concat(type.subTypes), [] as ActivitySubType[]);
	}
}
